import React, { useState, useEffect } from "react";
import { FaShoppingCart, FaStar, FaFilter, FaSearch, FaEye, FaEdit, FaTrash, FaPlus, FaTimes } from "react-icons/fa";
import axios from "../api/axios";

const Products = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [sortBy, setSortBy] = useState("name");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    filterAndSortProducts();
  }, [products, searchTerm, selectedCategory, sortBy]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get("/product/available");
      setProducts(res.data.products || []);
      
      // Extract unique categories
      const uniqueCategories = [...new Set((res.data.products || []).map(p => p.category).filter(Boolean))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortProducts = () => {
    let filtered = [...products];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by category
    if (selectedCategory !== "all") {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    // Sort products
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "price-low":
          return a.price - b.price;
        case "price-high":
          return b.price - a.price;
        case "name":
          return a.name.localeCompare(b.name);
        case "stock":
          return b.quantity - a.quantity;
        default:
          return 0;
      }
    });

    setFilteredProducts(filtered);
  };

  const handlePurchase = async (product) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        window.location.href = "/login";
        return;
      }

      if (quantity <= 0) {
        alert("Please enter a valid quantity");
        return;
      }

      if (quantity > product.quantity) {
        alert(`Only ${product.quantity} units available`);
        return;
      }

      const res = await axios.post(`/product/purchase/${product._id}`, {
        quantity,
        paymentMethod: "wallet_puap"
      });

      alert("Product purchased successfully!");
      fetchProducts(); // Refresh to update stock
      setShowModal(false);
      setQuantity(1);
    } catch (error) {
      console.error("Purchase error:", error);
      alert(error.response?.data?.message || "Purchase failed");
    }
  };

  const openProductModal = (product) => {
    setSelectedProduct(product);
    setQuantity(1);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Our Products</h1>
          <p className="text-gray-600">Discover our amazing collection of quality products</p>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            {/* Category Filter */}
            <div className="relative">
              <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none"
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="name">Sort by Name</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="stock">Stock Level</option>
            </select>

            {/* Results Count */}
            <div className="flex items-center justify-center text-gray-600">
              <span>{filteredProducts.length} products found</span>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-gray-400 text-6xl mb-4">
              <FaShoppingCart className="mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No products found</h3>
            <p className="text-gray-500">Try adjusting your filters or search terms</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <div key={product._id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300">
                {/* Product Image */}
                <div className="relative">
                  <img
                    src={product.images?.[0] || "https://via.placeholder.com/300x200"}
                    alt={product.name}
                    className="w-full h-48 object-cover"
                  />
                  {product.quantity <= 10 && product.quantity > 0 && (
                    <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                      Only {product.quantity} left!
                    </div>
                  )}
                  {product.quantity === 0 && (
                    <div className="absolute top-2 right-2 bg-gray-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                      Out of Stock
                    </div>
                  )}
                  <div className="absolute top-2 left-2 bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                    {product.category || "General"}
                  </div>
                </div>

                {/* Product Info */}
                <div className="p-4">
                  <h3 className="font-semibold text-lg text-gray-800 mb-2 line-clamp-2">
                    {product.name}
                  </h3>
                  
                  <div className="flex items-center mb-2">
                    <div className="flex text-yellow-400 text-sm">
                      {[...Array(5)].map((_, i) => (
                        <FaStar key={i} className={i < 4 ? "fill-current" : "text-gray-300"} />
                      ))}
                    </div>
                    <span className="ml-2 text-gray-600 text-sm">(4.0)</span>
                  </div>

                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {product.description || "High-quality product with excellent features."}
                  </p>

                  <div className="mb-3">
                    <div className="text-sm text-gray-500 mb-1">MRP: ₹{product.mrp || product.price}</div>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-2xl font-bold text-orange-600">₹{product.sp || product.price}</span>
                        <span className="text-gray-500 text-sm ml-1">/unit</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        Stock: <span className={`font-semibold ${product.quantity <= 10 ? 'text-red-600' : 'text-green-600'}`}>
                          {product.quantity}
                        </span>
                      </div>
                    </div>
                    <div className="text-sm text-blue-600 font-semibold mt-1">PV: {product.pv || product.price}</div>
                  </div>

                  {/* Commission Info */}
                  <div className="bg-blue-50 rounded p-2 mb-3 text-xs">
                    <div className="grid grid-cols-3 gap-1 text-center">
                      <div>
                        <span className="text-gray-600">Direct:</span>
                        <span className="block font-semibold text-green-600">{product.directCommission || 0}%</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Matrix:</span>
                        <span className="block font-semibold text-green-600">{product.matrixCommission || 0}%</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Gen:</span>
                        <span className="block font-semibold text-green-600">{product.generationCommission || 0}%</span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => openProductModal(product)}
                      className="flex-1 bg-orange-500 text-white py-2 px-3 rounded-lg hover:bg-orange-600 transition-colors flex items-center justify-center text-sm"
                      disabled={product.quantity === 0}
                    >
                      <FaShoppingCart className="mr-1" />
                      Buy
                    </button>
                    <button
                      onClick={() => openProductModal(product)}
                      className="bg-gray-200 text-gray-700 py-2 px-3 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      <FaEye />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Product Modal */}
      {showModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="relative">
              <button
                onClick={() => setShowModal(false)}
                className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 z-10"
              >
                <FaTimes size={24} />
              </button>

              <div className="grid md:grid-cols-2 gap-6 p-6">
                {/* Product Image */}
                <div>
                  <img
                    src={selectedProduct.images?.[0] || "https://via.placeholder.com/400x400"}
                    alt={selectedProduct.name}
                    className="w-full h-80 object-cover rounded-lg"
                  />
                </div>

                {/* Product Details */}
                <div>
                  <div className="mb-2">
                    <span className="bg-orange-100 text-orange-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                      {selectedProduct.category || "General"}
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-3">{selectedProduct.name}</h2>
                  
                  <div className="flex items-center mb-4">
                    <div className="flex text-yellow-400">
                      {[...Array(5)].map((_, i) => (
                        <FaStar key={i} className={i < 4 ? "fill-current" : "text-gray-300"} />
                      ))}
                    </div>
                    <span className="ml-2 text-gray-600 text-sm">(4.0)</span>
                  </div>

                  <p className="text-gray-600 mb-4">
                    {selectedProduct.description || "High-quality product with excellent features and benefits."}
                  </p>

                  <div className="mb-6">
                    <div className="text-sm text-gray-500 mb-1">MRP: ₹{selectedProduct.mrp || selectedProduct.price}</div>
                    <div className="flex items-baseline mb-2">
                      <span className="text-3xl font-bold text-orange-600">₹{selectedProduct.sp || selectedProduct.price}</span>
                      <span className="ml-2 text-gray-500">per unit</span>
                    </div>
                    <div className="text-blue-600 font-semibold mb-2">PV: {selectedProduct.pv || selectedProduct.price}</div>
                    <div className="text-sm text-gray-600">
                      Stock: <span className={`font-semibold ${selectedProduct.quantity <= 10 ? 'text-red-600' : 'text-green-600'}`}>
                        {selectedProduct.quantity} units available
                      </span>
                    </div>
                  </div>

                  {/* Quantity Selector */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Quantity:</label>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="bg-gray-200 text-gray-700 w-10 h-10 rounded-lg hover:bg-gray-300"
                      >
                        -
                      </button>
                      <input
                        type="number"
                        min="1"
                        max={selectedProduct.quantity}
                        value={quantity}
                        onChange={(e) => setQuantity(Math.min(selectedProduct.quantity, Math.max(1, parseInt(e.target.value) || 1)))}
                        className="w-20 text-center border border-gray-300 rounded-lg py-2"
                      />
                      <button
                        onClick={() => setQuantity(Math.min(selectedProduct.quantity, quantity + 1))}
                        className="bg-gray-200 text-gray-700 w-10 h-10 rounded-lg hover:bg-gray-300"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Total Price */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Total Price:</span>
                      <span className="text-2xl font-bold text-orange-600">₹{(selectedProduct.sp || selectedProduct.price) * quantity}</span>
                    </div>
                  </div>

                  {/* Purchase Button */}
                  <button
                    onClick={() => handlePurchase(selectedProduct)}
                    className="w-full bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors flex items-center justify-center"
                    disabled={selectedProduct.quantity === 0}
                  >
                    <FaShoppingCart className="mr-2" />
                    {selectedProduct.quantity === 0 ? "Out of Stock" : `Purchase for ₹${(selectedProduct.sp || selectedProduct.price) * quantity}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
